import { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Tenant feature flags, loaded once at startup and shared across the app so any
// component can gate a per-tenant feature with useFeature('key'). Flags are
// data in the tenant's own DB (jcrm_tenant_features) read via tenant-features.php
// — one shared codebase, per-tenant behaviour, no per-tenant builds.
//
// FEATURE_DEFAULTS mirrors the backend registry (includes/tenant_features.php
// TENANT_FEATURE_DEFAULTS) so the UI renders correctly before the fetch
// resolves and if it fails. Keep the two in sync when you add a flag.

const CHAT_API_BASE = '/api/chat';

export const FEATURE_DEFAULTS = {
  example_feature: false,
  scoped_example: false,
};

const FeaturesContext = createContext({
  features: FEATURE_DEFAULTS,
  meta: {},
  loading: true,
  isEnabled: () => false,
  reload: () => {},
});

export const useFeatures = () => useContext(FeaturesContext);

// Convenience: a single flag's boolean. `const showThing = useFeature('thing');`
export const useFeature = (key) => useContext(FeaturesContext).features[key];

export const FeaturesProvider = ({ children }) => {
  const [features, setFeatures] = useState(FEATURE_DEFAULTS);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${CHAT_API_BASE}/tenant-features.php`);
      const data = await res.json().catch(() => ({}));
      if (data && data.success && data.features) {
        const flat = {};
        Object.entries(data.features).forEach(([k, v]) => { flat[k] = !!v.enabled; });
        // Keep code defaults for any key the server didn't return, so a flag
        // referenced in the UI never comes back undefined.
        setFeatures({ ...FEATURE_DEFAULTS, ...flat });
        setMeta(data.features);
      }
    } catch (e) {
      /* keep defaults — the feature simply stays in its standard state */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const value = {
    features,
    meta,
    loading,
    isEnabled: (key) => !!features[key],
    reload: load,
  };

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
};

export default FeaturesContext;
